/**
 * Graphics-app and control-app configuration schemas (SPEC §19 "Config").
 *
 * Strict: every key is required, unknown or misspelled keys fail, and nothing
 * silently falls back to a default. Defaults live in the committed
 * `config.example.json`, never in this schema.
 *
 * Secrets are never stored inline. Each secret is a reference to a file or an
 * environment variable, resolved by the loader and never logged.
 */
import { z } from 'zod';
import { parseOrThrow } from './validate.ts';

export const GRAPHICS_CONFIG_SCHEMA_VERSION = 1;

/** Where a secret comes from. Exactly one of `file` or `env`. */
export const SecretRefSchema = z.union([
  z.strictObject({ file: z.string().min(1) }),
  z.strictObject({ env: z.string().regex(/^[A-Z_][A-Z0-9_]*$/, 'must be an UPPER_SNAKE environment variable name') }),
]);
export type SecretRef = z.infer<typeof SecretRefSchema>;

const port = z.number().int().min(1).max(65535);
const path = z.string().min(1);
const version = z.string().regex(/^\d+\.\d+\.\d+$/, 'must be a semantic version, e.g. "32.2.2"');

/** A half-open version range `[min, below)`. */
const versionRange = z
  .strictObject({ min: version, below: version })
  .refine((r) => compareVersions(r.min, r.below) < 0, { message: '"min" must be lower than "below"' });

const regexSource = z.string().min(1).refine(
  (src) => {
    try {
      new RegExp(src, 'i');
      return true;
    } catch {
      return false;
    }
  },
  { message: 'must be a valid regular expression' },
);

const header = z.string().trim().min(1).max(128);

/** obs-websocket server URL; both apps connect to the same OBS. */
const obsUrl = z.string().regex(/^wss?:\/\/[^\s/]+(:\d+)?\/?$/, 'must be a ws:// URL, e.g. "ws://127.0.0.1:4455"');

/**
 * Where the session data comes from: session files (event machines, ADR 0004), Google (live),
 * or a sheet-shaped JSON snapshot file (dev, tests, rehearsal).
 */
export const SheetsSourceSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('sessionFiles'),
    /** Folder of `<sessionId>.json` files (`pnpm sessions:export`); the admin editor writes here. */
    dir: path,
  }),
  z.strictObject({
    kind: z.literal('google'),
    spreadsheetId: z.string().min(1),
    sessionsTab: z.string().min(1),
    speakersTab: z.string().min(1),
    /** Google service-account key (read-only scope). Never committed. */
    serviceAccountKeyFile: path,
  }),
  z.strictObject({
    kind: z.literal('file'),
    /** JSON `{ "sessions": [[header…], [row…]…], "speakers": [[…]…] }`, the shape `values.batchGet` returns. */
    path,
  }),
]);
export type SheetsSource = z.infer<typeof SheetsSourceSchema>;

/**
 * Which sheet header holds each field (SPEC §6: read only the columns we use, never require a
 * rename). `null` for an optional column the sheet doesn't have.
 */
export const SheetColumnsSchema = z.strictObject({
  sessions: z.strictObject({
    sessionId: header,
    title: header,
    type: header,
    status: header,
    day: header,
    startTime: header,
    endTime: header,
    track: header,
    theme: header,
    location: header.nullable(),
    description: header.nullable(),
    tags: header.nullable(),
  }),
  speakers: z.strictObject({
    sessionId: header,
    speakerName: header,
    organization: header,
    /** The person's job title (the sheet calls it `title`). */
    jobTitle: header,
    speakerOrder: header,
  }),
});
export type SheetColumns = z.infer<typeof SheetColumnsSchema>;

export const GraphicsConfigSchema = z.strictObject({
  schemaVersion: z.literal(GRAPHICS_CONFIG_SCHEMA_VERSION),
  /** Local API and graphics surfaces (SPEC §3, §9.1). Bound to localhost only. */
  localApi: z.strictObject({
    host: z.literal('127.0.0.1', { error: 'the local API must bind to 127.0.0.1 only' }),
    port,
    token: SecretRefSchema,
    /**
     * Cross-origin callers allowed on `/api/*` and WebSocket upgrades, besides the
     * server's own origin, e.g. the control app's Tauri webview `http://tauri.localhost`.
     */
    allowedOrigins: z.array(z.string().regex(/^https?:\/\/[a-z0-9.-]+(:\d+)?$/, 'must be an origin like "http://tauri.localhost"')).max(8),
  }),
  obs: z.strictObject({
    url: obsUrl,
    password: SecretRefSchema,
  }),
  coreVideo: z.strictObject({
    host: z.string().min(1),
    port,
    /** CoreVideo's token is optional; `null` states explicitly that none is used. */
    token: SecretRefSchema.nullable(),
    /** CoreVideo has no push events: status, roster and outputs are polled. */
    pollIntervalMs: z.number().int().min(250).max(60_000),
    /** No answer within this time means the socket's response order can't be trusted: reconnect. */
    requestTimeoutMs: z.number().int().min(100).max(60_000),
  }),
  sheets: z
    .strictObject({
      source: SheetsSourceSchema,
      /**
       * Which sheet header holds each field. Required for the `google` and `file` sources;
       * not allowed with `sessionFiles`, where it would go unread.
       */
      columns: SheetColumnsSchema.optional(),
    })
    .superRefine((s, ctx) => {
      if (s.source.kind === 'sessionFiles' && s.columns !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['columns'], message: 'not used with a sessionFiles source; remove it' });
      } else if (s.source.kind !== 'sessionFiles' && s.columns === undefined) {
        ctx.addIssue({ code: 'custom', path: ['columns'], message: `required for a ${s.source.kind} source` });
      }
    }),
  graphics: z.strictObject({
    /** How long the auto lower third stays up when a Single scene goes on program (SPEC §9.4, §18). */
    lowerThirdAutoMs: z.number().int().min(500).max(60_000),
  }),
  /** Roster names ignored when counting and matching, matched case-insensitively (SPEC §5.4). */
  ignoredParticipantPatterns: z.array(regexSource).max(32),
  /** Paths are resolved relative to the config file. */
  paths: z.strictObject({
    fixture: path,
    sceneMap: path,
    layoutsDir: path,
    bundlesDir: path,
    /** lowdb project state and the JSONL action log. Never committed. */
    dataDir: path,
  }),
  /** Bundle ID under `paths.bundlesDir`. */
  bundle: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'must be a kebab-case bundle ID'),
  /** Preflight thresholds (SPEC §12, §18). Warnings, not blocks. */
  preflight: z.strictObject({
    minFreeDiskGb: z.number().positive(),
    minFeedWidth: z.number().int().positive(),
    minFeedHeight: z.number().int().positive(),
    testedVersions: z.strictObject({
      obs: versionRange,
      obsWebSocket: versionRange,
    }),
  }),
});
export type GraphicsConfig = z.infer<typeof GraphicsConfigSchema>;

/** Validates a parsed graphics config. @throws ValidationError listing every problem. */
export function parseGraphicsConfig(data: unknown, fileName = 'config.json'): GraphicsConfig {
  return parseOrThrow(GraphicsConfigSchema, data, `config file "${fileName}"`);
}

export const CONTROL_CONFIG_SCHEMA_VERSION = 1;

/**
 * Control-app configuration (SPEC §10, §19). The control app reads the same
 * `fixture.json` as the graphics app, from disk, so scene switching, audio and
 * outputs work while the graphics app is down. File paths and `file` secrets are
 * resolved relative to the config file.
 */
export const ControlConfigSchema = z.strictObject({
  schemaVersion: z.literal(CONTROL_CONFIG_SCHEMA_VERSION),
  obs: z.strictObject({
    url: obsUrl,
    password: SecretRefSchema,
    /** No answer within this time fails the request; the state stays unknown until OBS confirms. */
    requestTimeoutMs: z.number().int().min(250).max(30_000),
  }),
  /** The graphics app's local API (SPEC §3). */
  localApi: z.strictObject({
    url: z
      .string()
      .regex(/^http:\/\/(127\.0\.0\.1|localhost):\d{1,5}$/, 'the local API is served on 127.0.0.1 only, e.g. "http://127.0.0.1:4700"')
      .refine((u) => Number(u.slice(u.lastIndexOf(':') + 1)) <= 65535, { message: 'port must be 1-65535' }),
    token: SecretRefSchema,
    /** A command without a result in this time is retried with the same `commandId`. */
    commandTimeoutMs: z.number().int().min(500).max(30_000),
  }),
  paths: z.strictObject({
    /** The project's `fixture.json`, the same file the graphics app loads. */
    fixture: path,
  }),
  telemetry: z.strictObject({
    /** How often `GetStats` and `GetStreamStatus` are polled for the health strip. */
    statsIntervalMs: z.number().int().min(250).max(10_000),
    /** The health strip warns below this much free disk (SPEC §18: 50 GB, tune after rehearsal). */
    minFreeDiskGb: z.number().positive(),
  }),
});
export type ControlConfig = z.infer<typeof ControlConfigSchema>;

/** Validates a parsed control config. @throws ValidationError listing every problem. */
export function parseControlConfig(data: unknown, fileName = 'config.json'): ControlConfig {
  return parseOrThrow(ControlConfigSchema, data, `config file "${fileName}"`);
}

/** Compares two `x.y.z` versions: negative if a < b, 0 if equal, positive if a > b. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

/** True if `v` lies in `[range.min, range.below)`. */
export function versionInRange(v: string, range: { min: string; below: string }): boolean {
  return compareVersions(v, range.min) >= 0 && compareVersions(v, range.below) < 0;
}
